import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Avatar, Modal, Popover, tokens } from '@treams/treams-ui';

import TooltipDark from '../tooltips/TooltipDark';

const ExtraUsers = styled(Flex)<{ hovered: boolean; overlap?: boolean }>`
	cursor: pointer;
	width: 38px;
	height: 38px;
	background-color: ${tokens.surface.secondary};
	border-radius: 50%;
	transition: border 150ms ease-out;

	&:hover {
		border: 1px solid ${tokens.text.secondary};
	}

	${(props) =>
		props.hovered
			? `border: 1px solid ${tokens.text.secondary};`
			: `border: 1px solid ${props.theme.colors.lightBlueDark};`}

	p {
		font-size: 13px;
		line-height: 16px;
		color: ${tokens.text.secondary};
		margin-right: 0;
	}

	${(props) =>
		props.overlap &&
		`
		margin-left: -20px;
	`}
`;

const Element = styled(NavLink)<{ overlap?: boolean }>`
	position: relative;
	margin-right: 6px;
	display: inline-block;

	${(props) =>
		props.overlap &&
		`
		margin-left: -20px;
		:first-child {
			margin-left: 0;
		}

		// Add border to the avatar letter shape. To make the circles distinct
		> div > p {
			border: 1px solid ${props.theme.colors.white};
		}
	`}
`;

const StaticElement = styled.div<{ overlap?: boolean }>`
	position: relative;
	margin-right: 6px;
	display: inline-block;

	${(props) =>
		props.overlap &&
		`
		margin-left: -20px;
		:first-child {
			margin-left: 0;
		}

		// Add border to the avatar letter shape. To make the circles distinct
		> div > p {
			border: 1px solid ${props.theme.colors.white};
		}
	`}
`;

const Tooltip = styled(TooltipDark)`
	top: calc(100% + 8px);
	left: 50%;
	transform: translate(-50%, 0);
	max-width: 184px;

	&:after {
		top: -10px;
		left: 50%;
		transform: translate(-50%, 0);
		border-color: transparent transparent ${tokens.text.primary} transparent;
	}
`;

const TooltipPersonWrapper = styled(NavLink)`
	display: block;
	margin-bottom: 6px;

	&:first-child {
		margin-top: 4px;
	}
`;

const FullName = styled.p<{ color?: string }>`
	flex: 1;
	min-width: 0;
	color: ${(props) => props.theme.colors[props.color ?? 'white']};
	margin-left: 8px;

	span {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: block;
	}
`;

const Container = styled(Flex)`
	position: relative;

	p {
		margin-top: 0;
		pointer-events: none;
	}
`;
const TooltipPerson = ({ user }: { user: graphql.User }) => (
	<TooltipPersonWrapper to={`/users/${user.id}`}>
		<Flex alignItems="center">
			<Avatar user={user} size={32} />
			<FullName>
				<span>
					{user.firstName} {user.lastName}
				</span>
			</FullName>
		</Flex>
	</TooltipPersonWrapper>
);

function MemberAvatarsListModalItem(props: { user: graphql.User }) {
	return (
		<TooltipPersonWrapper to={`/users/${props.user.id}`}>
			<Flex alignItems="center">
				<Avatar user={props.user} size={32} />
				<FullName color="slateBlue">
					<span>
						{props.user.firstName} {props.user.lastName}
					</span>
				</FullName>
			</Flex>
		</TooltipPersonWrapper>
	);
}

export const MemberAvatarsItem = ({ user, overlap }: { user: graphql.User; overlap?: boolean }) => {
	const [isHovered, setIsHovered] = useState(false);

	const toggleHoverState = () => {
		setIsHovered(!isHovered);
	};

	const contents = (
		<>
			<div
				onMouseOver={() => toggleHoverState()}
				onMouseLeave={() => toggleHoverState()}
				onFocus={() => toggleHoverState()}
			>
				<Avatar user={user} size={32} />
			</div>
			<Tooltip isHovered={isHovered} direction="south">
				<span>
					{user.firstName} {user.lastName}
				</span>
			</Tooltip>
		</>
	);

	if (user.id) {
		return (
			<Element to={`/users/${user.id}`} overlap={overlap}>
				{contents}
			</Element>
		);
	} else {
		return <StaticElement overlap={overlap}>{contents}</StaticElement>;
	}
};

function MemberAvatarsListModal(props: {
	title: string;
	users: graphql.User[];
	onDismiss: () => void;
}) {
	return (
		<Popover center visible>
			<Modal>
				<Modal.Header title={props.title} onClose={props.onDismiss} />
				<Modal.Contents>
					{props.users.map((user) => (
						<MemberAvatarsListModalItem key={user.id} user={user} />
					))}
				</Modal.Contents>
			</Modal>
		</Popover>
	);
}

export const MemberAvatarsRestList = ({
	allUsers,
	restUsers,
	numberOfExtraUsers,
	overlap,
	title,
}: {
	allUsers: graphql.User[];
	restUsers: graphql.User[];
	numberOfExtraUsers: number;
	overlap?: boolean;
	title: string;
}) => {
	const [isHovered, setIsHovered] = useState(false);

	if (numberOfExtraUsers === 1) {
		return <MemberAvatarsItem user={restUsers[0]} />;
	}

	const toggleHoverState = () => {
		setIsHovered(!isHovered);
	};

	return (
		<Container>
			<ExtraUsers
				overlap={overlap}
				alignItems="center"
				justifyContent="center"
				onClick={() => toggleHoverState()}
				hovered={isHovered}
			>
				<p>+{numberOfExtraUsers}</p>
			</ExtraUsers>
			<Tooltip isHovered={isHovered && restUsers.length <= 5} direction="south">
				<div>
					{restUsers.map((user) => (
						<TooltipPerson key={user.id} user={user} />
					))}
				</div>
			</Tooltip>
			{isHovered && restUsers.length > 5 && (
				<MemberAvatarsListModal
					title={title}
					onDismiss={() => setIsHovered(false)}
					users={allUsers}
				/>
			)}
		</Container>
	);
};
